import { useQuery } from '@tanstack/react-query';
import { regionSettingsService, type Region } from '../services/region-settings.service';

interface UseRegionResult {
  region: Region | null;
  /**
   * True when the installation operates under the EU framework — or while the
   * region is still loading/unknown (default-to-visible, same philosophy as
   * useTenantModules). EU-only surfaces hide only once we positively know the
   * platform region is non-EU.
   */
  isEU: boolean;
  isLoading: boolean;
}

/**
 * Resolves the installation-wide platform region (Settings → Region & Privacy).
 * Drives box-level hiding of EU-only regulations/features (EU AI Act, GDPR) on
 * non-EU installations. Endpoint is public and cached for 5 minutes.
 */
export function useRegion(): UseRegionResult {
  const { data, isLoading } = useQuery({
    queryKey: ['platform-region'],
    queryFn: () => regionSettingsService.getPlatformRegion(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const region = data?.region ?? null;
  return {
    region,
    isEU: region === null || region === 'EU',
    isLoading,
  };
}

export default useRegion;
